import { Pin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DEFAULT_HERO, getTheme, type Theme } from '@/features/theme/themes';
import { themeAssetUrl } from '@/features/theme/themeAssets';
import { useThemeLineup } from '@/features/theme/themeLineup';
import { ThemeThumbnail } from '@/features/theme/components/ThemeThumbnail';

/** How many pinned-theme previews a strip surfaces. */
const PINNED_SLOTS = 3;

/**
 * A still, thumbnail-scale snapshot of the WINR landing hero rendered under one
 * theme — the theme image behind the wordmark + headline + CTAs, exactly the top
 * of `LandingPage` minus the featured-communities row. Deliberately static: no
 * wordmark pan, no grain, no arrow pulse — just a frozen frame of "what it could
 * look like". The theme's image is clipped into the WINR letters (its `--hero-bg`)
 * and used as the backdrop; the imageless default falls back to the blue gradient.
 */
function ThemeLandingPreview({ theme }: { theme: Theme }) {
  const url = themeAssetUrl(theme.file);
  const heroFill = url ? `url('${url}')` : DEFAULT_HERO;

  return (
    <span
      title={`Pinned theme: ${theme.name}`}
      className="relative block aspect-[16/10] flex-1 overflow-hidden rounded-lg border border-white/20 shadow-[0_2px_10px_rgba(0,0,0,0.55)]"
    >
      {/* Still theme backdrop (ThemeThumbnail renders the image only — no anim). */}
      <ThemeThumbnail theme={theme} className="absolute inset-0" />
      {/* Readability scrim, mirroring the landing page's background overlay. */}
      <span className="absolute inset-0 bg-black/55" />

      {/* Frozen hero content */}
      <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-2 text-center">
        <span
          className="bg-clip-text text-[34px] font-black leading-none tracking-[-2px] text-transparent [-webkit-text-fill-color:transparent] [-webkit-text-stroke:1.5px_#fff] [font-family:'Arial_Black',Impact,sans-serif]"
          style={{
            backgroundImage: heroFill,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          WINR
        </span>
        <span className="text-[9px] font-black leading-tight tracking-[-0.2px] text-white">
          The Home for Every Community.
        </span>
        <span className="mt-0.5 flex items-center gap-1.5">
          <span className="rounded-pill border border-white/20 bg-white/10 px-2 py-0.5 text-[7px] font-semibold text-white/80">
            Learn more
          </span>
          <span className="rounded-sm bg-white px-2 py-0.5 text-[7px] font-bold text-page">
            Create
          </span>
        </span>
      </span>
    </span>
  );
}

/**
 * A row of still landing-hero previews for the first `PINNED_SLOTS` pinned themes.
 * Reads the same `useThemeLineup` store the Locker's Pin button writes to, so
 * pinning/unpinning reflects here live. Always renders `PINNED_SLOTS` slots —
 * unpinned ones read as dashed placeholders. Positioning is left to the parent
 * (the hero card overlays it top-center; the editor centers it in its top
 * deadspace), so this only owns the row layout.
 */
export function PinnedThemePreviews({ className }: { className?: string }) {
  const lineup = useThemeLineup((s) => s.lineup);
  const pinned = lineup.slice(0, PINNED_SLOTS);

  return (
    <div className={cn('flex w-full items-stretch gap-3', className)}>
      {Array.from({ length: PINNED_SLOTS }).map((_, i) => {
        const theme = pinned[i] ? getTheme(pinned[i]) : undefined;
        return theme ? (
          <ThemeLandingPreview key={theme.id} theme={theme} />
        ) : (
          <span
            key={`empty-${i}`}
            title="Pin a theme from your Locker"
            className="grid aspect-[16/10] flex-1 place-items-center rounded-lg border border-dashed border-white/20 bg-black/30 text-white/40"
          >
            <Pin size={16} strokeWidth={2.2} />
          </span>
        );
      })}
    </div>
  );
}
